import { useRef } from 'react'

import { albumByIdQuery, albumTrackPositionUpdate } from '@/entities/albums'
import {
  TrackUploadStatus,
  albumTracksDeleted,
  trackDeleted,
} from '@/entities/tracks'
import type { UpdateAlbumTrackOrderBody } from '@/shared/api'
import { showDeleteConfirmationModal } from '@/shared/lib/antd-utils'
import { COMMON_COLUMN_WIDTH } from '@/shared/lib/styling'
import { EmptyContent, HidingSpin, TableDragHandle } from '@/shared/ui'
import {
  CloseCircleTwoTone,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleTwoTone,
  LoadingOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { DragSortTable } from '@ant-design/pro-components'
import type { ProColumns } from '@ant-design/pro-components'
import { Button, Flex, Space, Spin, Tooltip, Upload, theme } from 'antd'
import { useUnit } from 'effector-react'

import {
  $albumTracksReorderBlocked,
  $albumTracksWithProgressReordered,
  type AlbumTrackWithStatus,
  addAlbumTrackMultiuploadFiles,
  albumTracksReordered,
  tracksAlbumTracksQuery,
} from '../model/album-track-multiupload.model'

interface AlbumFormTracksProps {
  onTrackEditButtonClick: (id: string) => void
}

export const AlbumFormTracks = ({
  onTrackEditButtonClick,
}: AlbumFormTracksProps) => {
  const { token: antDesignThemeToken } = theme.useToken()

  // Data

  const albumData = useUnit(albumByIdQuery.$data)
  const albumError = useUnit(albumByIdQuery.$error)
  const albumTracksData = useUnit(tracksAlbumTracksQuery.$data)
  const albumTracksError = useUnit(tracksAlbumTracksQuery.$error)
  const albumTracksList = useUnit($albumTracksWithProgressReordered)

  // Delete all tracks
  const deleteAlbumTracks = useUnit(albumTracksDeleted)
  const handleDeleteAlbumTracks = () => {
    if (!albumData) return
    showDeleteConfirmationModal({
      onConfirm: () => {
        deleteAlbumTracks({ albumId: albumData.id })
      },
      options: {
        title: 'Удаление',
        content: 'Вы уверены, что хотите удалить все треки в альбоме?',
        okText: 'Удалить',
      },
    })
  }

  // Tracks files upload
  const addFiles = useUnit(addAlbumTrackMultiuploadFiles)

  // Refs
  const uploadRef = useRef<HTMLDivElement>(null)

  // Album Tracks Reorder
  const updateTrackPositionInAlbum = useUnit(albumTrackPositionUpdate)
  const reorderAlbumTracks = useUnit(albumTracksReordered)
  const handleDragSortEnd = (
    _beforeIndex: number,
    _afterIndex: number,
    albumTracksListReordered: AlbumTrackWithStatus[],
  ) => {
    if (albumData === null) return

    const originalIndexMap = new Map<string, number>()
    albumTracksList.forEach((track, idx) => {
      originalIndexMap.set(track.id, idx)
    })

    const changedTracks: UpdateAlbumTrackOrderBody[] = []

    albumTracksListReordered.forEach((track, newIndex) => {
      const oldIndex = originalIndexMap.get(track.id)
      if (oldIndex === undefined) return
      if (oldIndex !== newIndex) {
        changedTracks.push({
          trackId: track.id,
          rowNumber: newIndex,
        })
      }
    })

    reorderAlbumTracks(albumTracksListReordered)

    updateTrackPositionInAlbum.start({
      id: albumData.id,
      data: changedTracks,
    })
  }

  // Track Form Modal
  const deleteTrack = useUnit(trackDeleted)
  const handleDelete = (id: string) => {
    showDeleteConfirmationModal({
      onConfirm: () => {
        deleteTrack({ id })
      },
    })
  }

  // Table Columns
  const albumTracksReorderBlocked = useUnit($albumTracksReorderBlocked)
  const columns: ProColumns<AlbumTrackWithStatus>[] = [
    {
      dataIndex: 'sort',
      width: COMMON_COLUMN_WIDTH.DRAG,
      render: () => null,
      onCell: () => ({
        style: {
          backgroundColor: '#fafafa',
          pointerEvents: albumTracksReorderBlocked ? 'none' : 'auto',
          opacity: albumTracksReorderBlocked ? 0.3 : 1,
        },
      }),
    },
    {
      title: 'Наименование',
      dataIndex: 'name',
      width: '50%',
      render: (_, track) => {
        if (track.fileUploadStatus === 'done') {
          if (track.uploadStatus === TrackUploadStatus.Uploading) {
            return (
              <Flex gap={16}>
                <Tooltip title="Файл не загружен">
                  <ExclamationCircleTwoTone
                    twoToneColor={antDesignThemeToken.colorError}
                  />
                </Tooltip>
                {track.name}
              </Flex>
            )
          } else if (track.uploadStatus === TrackUploadStatus.DeletionPending) {
            return (
              <Flex gap={16}>
                <Tooltip title="Помечен на удаление">
                  <CloseCircleTwoTone
                    twoToneColor={antDesignThemeToken.colorWarning}
                  />
                </Tooltip>
                {track.name}
              </Flex>
            )
          } else return track.name
        } else {
          return (
            <Flex gap={12}>
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: '42px' }} />}
              />
              {track.name}
            </Flex>
          )
        }
      },
    },
    {
      title: 'Автор',
      width: '50%',
      dataIndex: 'author',
      key: 'author',
      render: (_, track) => {
        return track.authors.map((a) => a.nickname).join(', ')
      },
    },
    {
      title: (
        <Button
          onClick={handleDeleteAlbumTracks}
          icon={<DeleteOutlined style={{ color: 'red' }} />}
        >
          Удалить все
        </Button>
      ),
      width: COMMON_COLUMN_WIDTH.ACTIONS,
      key: 'actions',
      align: 'center',
      render: (_, track) => {
        return (
          <Space size="middle">
            <Button
              onClick={() => onTrackEditButtonClick(track.id)}
              icon={<EditOutlined />}
            />
            <Button
              onClick={() => handleDelete(track.id)}
              icon={<DeleteOutlined style={{ color: 'red' }} />}
            />
          </Space>
        )
      },
    },
  ]

  return (
    <>
      {albumData?.type !== undefined ? (
        <HidingSpin
          spinning={
            (!albumData && !albumError) ||
            (!albumTracksData && !albumTracksError)
          }
        >
          {albumTracksList.length ? (
            <>
              <Flex vertical gap={8} style={{ width: '100%' }}>
                <DragSortTable
                  options={false}
                  columns={columns}
                  rowKey="id"
                  search={false}
                  pagination={false}
                  dataSource={albumTracksList}
                  dragSortKey="sort"
                  dragSortHandlerRender={TableDragHandle}
                  onDragSortEnd={handleDragSortEnd}
                />

                <Button
                  type="default"
                  style={{ width: '100%' }}
                  onClick={() => {
                    if (!uploadRef.current) return
                    uploadRef.current.click()
                  }}
                  icon={<PlusOutlined />}
                >
                  Загрузить треки
                </Button>
              </Flex>
            </>
          ) : (
            <EmptyContent
              description="В списке пока нет ни одного трека"
              content={
                <Space>
                  <Button
                    type="default"
                    onClick={() => {
                      if (!uploadRef.current) return
                      uploadRef.current.click()
                    }}
                  >
                    Загрузить треки
                  </Button>
                </Space>
              }
            />
          )}
          <Upload
            multiple
            accept=".mp3"
            beforeUpload={(_file, fileList) => {
              const fileListUids = fileList.map((file) => file.uid)
              if (fileListUids.every((uid) => typeof uid === 'string')) {
                addFiles(fileList)
              }
              return false
            }}
            fileList={[]}
            showUploadList={false}
            style={{ width: '100%', display: 'none' }}
          >
            <div ref={uploadRef} />
          </Upload>
        </HidingSpin>
      ) : null}
    </>
  )
}
